import { cloneDeep } from "lodash";
import * as client from "../../client";
import { ClientId, featureEnabled } from "../../client-features";

export type SafeSOV = Omit<client.Sov, "addresses"> & {
  addresses: client.RawAddress[];
  unsafeAddresses: client.Address[];
};
export type SafeCommercialReport = Omit<
  client.CommercialReport,
  "sovs" | "company_info"
> & {
  sovs: SafeSOV[];
  company_info: client.RawCompanyInfo;
  original_raw_company_info: client.CompanyInfo;
};

export type SafeReportMetadata = Omit<
  client.CommericalReportMetadata,
  "company_info"
> & {
  company_info: client.RawCompanyInfo;
  original_raw_company_info: client.CompanyInfo;
};

export type SafeReportResponse = Omit<client.ReportResponse, "report_json"> & {
  report_json: SafeCommercialReport;
};

export type SafeReportMetadataResponse = Omit<
  client.ReportMetadataResponse,
  "report_metadata"
> & {
  report_metadata: SafeReportMetadata;
};

export const applyOverridesToAddressField = <K extends keyof client.RawAddress>(
  address: client.Address,
  k: K
): client.RawAddress[K] => {
  return k in address.private_overrides
    ? (address.private_overrides[k] as client.RawAddress[K])
    : address.private_computed_values[k];
};

export const applyOverridesToAddress = (
  address: client.Address
): client.RawAddress => {
  // Probably common case: no overrides.
  if (Object.keys(address.private_overrides).length === 0) {
    return address.private_computed_values;
  }

  return Object.fromEntries(
    Object.entries(address.private_computed_values).map(([k, v]) => [
      k,
      k in address.private_overrides ? address.private_overrides[k] : v,
    ])
  ) as client.RawAddress;
};

export const applyOverridesToSov = (sov: client.Sov): SafeSOV => {
  return {
    sov_id: sov.sov_id,
    is_enabled: sov.is_enabled,
    filename: sov.filename,
    all_document_headers: sov.all_document_headers,
    column_mappings: sov.column_mappings,
    column_overrides: sov.column_overrides,
    addresses: sov.addresses.map(applyOverridesToAddress),
    unsafeAddresses: sov.addresses,
  };
};

export const applyOverridesToCompanyInfo = (
  companyInfo: client.CompanyInfo,
  clientId: ClientId
): client.RawCompanyInfo => {
  const safeResponse = Object.fromEntries(
    Object.entries(companyInfo.private_computed_values).map(([k, v]) => [
      k,
      k in companyInfo.private_overrides ? companyInfo.private_overrides[k] : v,
    ])
  ) as client.RawCompanyInfo;

  if (featureEnabled(clientId, "use-named-insured-as-company-name")) {
    safeResponse.company_name = safeResponse.named_insured ?? "";
  }

  return safeResponse;
};

export const applyOverridesToReport = (
  report: client.CommercialReport,
  clientId: ClientId
): SafeCommercialReport => {
  return {
    ...report,
    company_info: applyOverridesToCompanyInfo(report.company_info, clientId),
    sovs: report.sovs.map(applyOverridesToSov),
    original_raw_company_info: report.company_info,
  };
};

export const applyOverridesToMetadata = (
  reportMetadata: client.CommericalReportMetadata,
  clientId: ClientId
): SafeReportMetadata => {
  return {
    ...reportMetadata,
    company_info: applyOverridesToCompanyInfo(
      reportMetadata.company_info,
      clientId
    ),
    original_raw_company_info: reportMetadata.company_info,
  };
};

export const applyOverridesToMetadataReponse = (
  reportMetadata: client.ReportMetadataResponse,
  clientId: ClientId
): SafeReportMetadataResponse => {
  return {
    ...reportMetadata,
    report_metadata: applyOverridesToMetadata(
      reportMetadata.report_metadata,
      clientId
    ),
  };
};

export const applyOverridesToResponse = (
  response: client.ReportResponse,
  clientId: ClientId
): SafeReportResponse => {
  return {
    ...response,
    report_json: applyOverridesToReport(response.report_json, clientId),
  };
};

// Backend interprets override of exactly lat and lon as a map pin override and
// preserves it.  We manually clear lat/lon when doing address edits to avoid
// that case.  If we didn't do this, then editing and reverting a single field
// like state would leave lat/lon permanently overridden.
const FIELDS_THAT_CLEAR_LAT_LONG: (keyof client.RawAddress)[] = [
  "street_address",
  "city",
  "state",
  "zip",
  "county",
  "country",
];
export const applyDeltasAsOverride = (
  old: client.Address,
  edited: client.RawAddress
): { newAddress: client.Address; fieldNameEdits: string[] } => {
  const currentEffective = applyOverridesToAddress(old);
  const copy = cloneDeep(old);
  const fieldNameEdits: string[] = [];
  for (const _key of Object.keys(edited)) {
    const key = _key as keyof client.RawAddress;
    if (edited[key] !== currentEffective[key]) {
      copy.private_overrides[key] = edited[key] ?? null;
      fieldNameEdits.push(key);

      // Drop overrides that have no effect
      if (copy.private_overrides[key] === copy.private_computed_values[key]) {
        delete copy.private_overrides[key];
      }

      if (FIELDS_THAT_CLEAR_LAT_LONG.includes(key)) {
        delete copy.private_overrides["lat"];
        delete copy.private_overrides["long"];
      }
    }
  }
  return { newAddress: copy, fieldNameEdits };
};
